import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { IQueryParam, IFilter, ILogEvento } from 'modelos/src';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IColumnas } from '../../../../../../auxiliares/tabla/tabla.component';
import { LoginService } from '../../../../../login/login.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { LogEventoLoraService } from '../../../../../../auxiliares/servicios/http/log-evento-lora.service';
import { ActivatedRoute } from '@angular/router';
import { PayloadParserService } from '../../../../../../auxiliares/payload-parser-dialog/payload-parser.service';
import {
  fechaCelda,
  haceCelda,
} from '../../../../../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../../../../../auxiliares/html-seguro.util';

@Component({
  selector: 'app-listado-eventos-lora',
  templateUrl: './listado-eventos-lora.component.html',
  styleUrl: './listado-eventos-lora.component.scss',
  standalone: false,
})
export class ListadoEventosLoraComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  matDialog = inject(MatDialog);
  logEventoMqttService = inject(LogEventoLoraService);
  private payloadParserService = inject(PayloadParserService);

  public loading = false;
  public name = 'ListadoEventosLoraLuminariaComponent';

  @Input() deveui: string;

  public logEvento?: ILogEvento[];
  public totalCount = 0;
  public columnas?: IColumnas<ILogEvento>[];

  //Subs
  public logEvento$?: Subscription;

  //Buscar
  // public search: IRegExpSearch = {
  //   fields: ['deveui', 'deviceName'],
  // };

  // FILTROS
  // Rango de fechas
  private fechaCreacion: IFiltroTabla = {
    filter: {
      field: 'fechaCreacion',
    },
    label: 'Rango de fechas',
    tipo: 'dateRange',
  };
  // Tipo de evento
  private tipo: IFiltroTabla = {
    elementos: ['up', 'status', 'join', 'ack', 'txack'],
    filter: {
      field: 'tipo',
    },
    label: 'Tipo de evento',
    tipo: 'ngselect',
  };
  // Puerto
  private puerto: IFiltroTabla = {
    filter: {
      field: 'puerto',
    },
    label: 'Puerto',
    tipo: 'input',
  };
  public filtros: IFiltroTabla[] = [this.fechaCreacion, this.tipo, this.puerto];

  public query: IQueryParam = {
    sort: '-fechaCreacion',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  public async listar(): Promise<void> {
    const filter = JSON.parse(this.query.filter || '{}');
    filter['deveui'] = this.deveui;
    this.query.filter = JSON.stringify(filter);

    //
    this.logEvento$?.unsubscribe();
    this.logEvento$ = this.lista2
      .logEventos(this.query, 15)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.logEvento = data.datos;
      });
  }

  public parsearPayload(dato: ILogEvento): void {
    if (dato.payload && typeof dato.payload === 'string') {
      this.payloadParserService.openPayloadParser(dato.payload);
    } else {
      console.warn('No hay payload válido para parsear:', dato);
    }
  }

  /////////////////////////////////////////////////////////////
  // Tabla

  private setColumnas() {
    const columnas: IColumnas<ILogEvento>[] = [
      // Fecha
      {
        header: {
          label: 'Fecha',
          sort: 'fechaCreacion',
          sortable: true,
        },
        row: {
          // Es un log: los segundos importan y van en el tooltip
          html: (dato) => {
            const f = fechaCelda(dato.fechaCreacion);
            if (!f) return '';
            return `<div class="tabla-celda-col"><span>${f}</span><span class="tabla-secundario">${haceCelda(dato.fechaCreacion)}</span></div>`;
          },
          tooltip: (dato) => {
            const f = fechaCelda(dato.fechaCreacion);
            if (!f) return undefined;
            const seg = `0${new Date(dato.fechaCreacion!).getSeconds()}`.slice(-2);
            return `<div>${f}:${seg}</div>`;
          },
          noData: 'Sin fecha',
        },
      },

      // Tipo
      {
        header: {
          label: 'Tipo',
        },
        row: {
          field: 'tipo',
          noData: 'Sin tipo',
        },
      },

      // deveui
      {
        header: {
          label: 'DevEUI',
        },
        row: {
          field: 'deveui',
          claseCss: () => 'tabla-codigo',
          noData: 'Sin DevEUI',
        },
      },

      // Dispositivo
      {
        header: {
          label: 'Dispositivo',
        },
        row: {
          field: 'deviceName',
          noData: 'Sin dispositivo',
        },
      },

      // Puerto
      {
        header: {
          label: 'Puerto',
        },
        row: {
          field: 'puerto',
          noData: 'Sin puerto',
        },
      },

      // FCnt
      {
        header: {
          label: 'FCnt',
        },
        row: {
          field: 'fCnt',
          noData: 'Sin FCnt',
        },
      },
      // Payload/Margin
      {
        header: {
          label: 'Payload / margen',
        },
        row: {
          html(dato) {
            if (dato.tipo === 'up') {
              return dato.payload
                ? `<span class="tabla-codigo">${escaparHtml(dato.payload)}</span>`
                : '';
            } else if (dato.tipo === 'status') {
              return dato.margin !== undefined && dato.margin !== null
                ? `<span>Margen: ${escaparHtml(String(dato.margin))}</span>`
                : '';
            } else {
              return '';
            }
          },
          noData: 'Sin payload',
        },
      },
      // Batería
      {
        header: {
          label: 'Batería',
        },
        row: {
          field: 'battery',
          noData: 'Sin batería',
        },
      },
      // Acciones
      {
        header: {
          label: 'Acciones',
        },
        row: {
          field: 'acciones',
          acciones: [
            // {
            //   icon: 'visibility',
            //   tooltip: 'Ver detalles',
            //   click: (dato) => this.verDetalles(dato),
            // },
            {
              icon: 'visibility',
              tooltip: 'Ver payload decodificado',
              click: (dato) => this.parsearPayload(dato),
              oculta: (dato) => dato.tipo !== 'up' && dato.tipo !== 'down',
            },
          ],
        },
      },
    ];

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.logEvento$?.unsubscribe();
  }
}
